import type { Prisma, PrismaClient } from '@prisma/client';
import { z } from 'zod';

type Db = PrismaClient | Prisma.TransactionClient;

export const ITEM_TYPES = ['service', 'product', 'domain', 'hosting', 'subscription'] as const;
export const REVENUE_KEYS = [
  'REV_SOFTWARE',
  'REV_CONSULT',
  'REV_HOSTING',
  'REV_EQUIP',
  'REV_OTHER',
] as const;

/** Sensible revenue account per item type (changeable per item). */
export const DEFAULT_REVENUE: Record<(typeof ITEM_TYPES)[number], (typeof REVENUE_KEYS)[number]> = {
  service: 'REV_CONSULT',
  product: 'REV_EQUIP',
  domain: 'REV_HOSTING',
  hosting: 'REV_HOSTING',
  subscription: 'REV_HOSTING',
};

const money = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim())
  .refine((v) => v === '' || /^\d{1,15}(\.\d{1,4})?$/.test(v), 'Enter a valid amount')
  .transform((v) => (v === '' ? null : v));

const priceSchema = z.object({
  currency: z
    .string()
    .trim()
    .length(3)
    .transform((v) => v.toUpperCase()),
  price: money.refine((v) => v !== null, 'Price is required') as z.ZodType<string>,
});

export const itemInputSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(200),
  sku: z
    .string()
    .trim()
    .max(60)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  type: z.enum(ITEM_TYPES).default('service'),
  description: z
    .string()
    .trim()
    .max(1000)
    .transform((v) => v || null)
    .nullable()
    .optional(),
  unit: z.string().trim().min(1).max(20).default('each'),
  vatCode: z.string().trim().min(2).max(30).default('VAT_STD'),
  revenueAccountKey: z.enum(REVENUE_KEYS).optional(),
  costPrice: money.nullable().optional(),
  active: z.boolean().default(true),
  prices: z.array(priceSchema).max(10).default([]),
  customFields: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
});
export type ItemInput = z.infer<typeof itemInputSchema>;

export const itemInclude = { prices: true } satisfies Prisma.ItemInclude;
export type ItemWithPrices = Prisma.ItemGetPayload<{ include: typeof itemInclude }>;

export function itemView(i: ItemWithPrices) {
  return {
    id: i.id,
    name: i.name,
    sku: i.sku,
    type: i.type,
    description: i.description,
    unit: i.unit,
    vatCode: i.vatCode,
    revenueAccountKey: i.revenueAccountKey,
    costPrice: i.costPrice ? i.costPrice.toFixed(4) : null,
    active: i.active,
    needsReview: i.needsReview,
    customFields: i.customFields,
    prices: Object.fromEntries(i.prices.map((p) => [p.currency, p.price.toFixed(4)])),
    updatedAt: i.updatedAt,
  };
}

/** Errors that need the database: the VAT category and price currencies must exist. */
export async function itemReferenceErrors(
  db: Db,
  companyId: number,
  input: Pick<ItemInput, 'vatCode' | 'prices'>,
): Promise<string[]> {
  const errors: string[] = [];
  const vat = await db.taxRate.findFirst({
    where: { companyId, code: input.vatCode, kind: 'vat', active: true },
  });
  if (!vat) errors.push(`VAT category "${input.vatCode}" does not exist`);
  for (const p of input.prices) {
    if (!(await db.currency.findUnique({ where: { code: p.currency } })))
      errors.push(`Unknown currency "${p.currency}"`);
  }
  return errors;
}

export async function saveItem(
  tx: Db,
  args: {
    companyId: number;
    userId: number;
    id?: number;
    input: Partial<ItemInput> & { name?: string };
    needsReview?: boolean;
  },
): Promise<ItemWithPrices> {
  const { input } = args;
  const type = (input.type ?? 'service') as (typeof ITEM_TYPES)[number];
  const data = {
    ...(input.name !== undefined ? { name: input.name } : {}),
    ...(input.sku !== undefined ? { sku: input.sku } : {}),
    ...(input.type !== undefined ? { type: input.type } : {}),
    ...(input.description !== undefined ? { description: input.description } : {}),
    ...(input.unit !== undefined ? { unit: input.unit } : {}),
    ...(input.vatCode !== undefined ? { vatCode: input.vatCode } : {}),
    ...(input.revenueAccountKey !== undefined
      ? { revenueAccountKey: input.revenueAccountKey }
      : args.id
        ? {}
        : { revenueAccountKey: DEFAULT_REVENUE[type] }),
    ...(input.costPrice !== undefined ? { costPrice: input.costPrice } : {}),
    ...(input.active !== undefined ? { active: input.active } : {}),
    ...(input.customFields !== undefined ? { customFields: input.customFields } : {}),
    updatedBy: args.userId,
  };
  const item = args.id
    ? await tx.item.update({ where: { id: args.id }, data })
    : await tx.item.create({
        data: {
          ...(data as Omit<Prisma.ItemUncheckedCreateInput, 'companyId'>),
          companyId: args.companyId,
          createdBy: args.userId,
          needsReview: args.needsReview ?? false,
        } as Prisma.ItemUncheckedCreateInput,
      });
  if (input.prices) {
    await tx.itemPrice.deleteMany({ where: { itemId: item.id } });
    if (input.prices.length) {
      await tx.itemPrice.createMany({
        data: input.prices.map((p) => ({ itemId: item.id, currency: p.currency, price: p.price })),
      });
    }
  }
  return tx.item.findUniqueOrThrow({ where: { id: item.id }, include: itemInclude });
}
